import type { RouterInputs, RouterOutputs } from '@client/trpc/client';
import type { DisplayColor } from '@shared/constants/business/display-configuration';
import { GLOBAL_SEARCH_QUERY_MAX_LENGTH } from '@shared/constants/system/general';
import { match } from 'ts-pattern';

type GlobalSearchInput = RouterInputs['global']['globalSearch'];
type GlobalSearchOutput = RouterOutputs['global']['globalSearch'];

type ClaimWhere = NonNullable<GlobalSearchInput['claim']>['where'];
type InvoiceWhere = NonNullable<GlobalSearchInput['invoice']>['where'];
type IncidentWhere = NonNullable<GlobalSearchInput['incident']>['where'];
type ComplaintWhere = NonNullable<GlobalSearchInput['complaint']>['where'];
type RiskWhere = NonNullable<GlobalSearchInput['risk']>['where'];
type ControlWhere = NonNullable<GlobalSearchInput['control']>['where'];
type ControlTestWhere = NonNullable<GlobalSearchInput['controlTest']>['where'];
type ObligationWhere = NonNullable<GlobalSearchInput['obligation']>['where'];

/**
 * Below this the server round-trip is pure noise — the same three-character
 * floor the retired top-bar search always applied.
 */
export const RECORD_SEARCH_MIN_QUERY_LENGTH = 3;

/**
 * Upper bound on how many words a query contributes to the AND-across-words match. Nobody
 * identifies a record by more than a handful of words, but a pasted paragraph would otherwise fan
 * out into hundreds of ILIKE predicates across eight entities on every debounce tick. Words beyond
 * the cap are ignored (the first ones carry the intent); the server's clause budget in
 * `server/src/schemas/filters/define.ts` enforces the same bound against any other caller.
 */
export const RECORD_SEARCH_MAX_WORDS = 8;

export interface ClaimSearchResult {
    entity: 'claim';
    id: bigint;
    /** `generatedId` when the claim has one, otherwise its numeric id. */
    reference: string;
    statusName: string;
    statusDisplayColor: DisplayColor;
    owner: { userId: bigint; fullName: string; photoUrl: string | null } | undefined;
}

export interface InvoiceSearchResult {
    entity: 'invoice';
    /** Invoices open their claim's financials tab — they have no page of their own. */
    claimId: bigint;
    invoiceNo: string;
}

export interface IncidentSearchResult {
    entity: 'incident';
    /** Incidents have no reference string — the numeric id is the identity users know. */
    id: bigint;
    description: string;
    status: string | null;
}

export interface ComplaintSearchResult {
    entity: 'complaint';
    id: bigint;
    /** "First Last" from whichever complainant name parts exist; '' when both are missing. */
    complainantName: string;
    description: string | null;
    status: string | null;
}

export interface RiskSearchResult {
    entity: 'risk';
    id: bigint;
    title: string;
}

export interface ControlSearchResult {
    entity: 'control';
    id: bigint;
    name: string;
}

export interface ControlTestSearchResult {
    entity: 'controlTest';
    id: bigint;
    /** Tests are only addressable under their parent control's detail route. */
    controlId: bigint;
    name: string;
    controlName: string;
}

export interface ObligationSearchResult {
    entity: 'obligation';
    id: bigint;
    /** `identificationCode` when set, otherwise the numeric id. */
    reference: string;
    coreObligation: string | null;
}

export type RecordSearchResult =
    | ClaimSearchResult
    | InvoiceSearchResult
    | IncidentSearchResult
    | ComplaintSearchResult
    | RiskSearchResult
    | ControlSearchResult
    | ControlTestSearchResult
    | ObligationSearchResult;

export interface RecordSearchSection {
    entity: RecordSearchResult['entity'];
    label: string;
    results: RecordSearchResult[];
}

/**
 * The exact-id value an all-digit query resolves to, or `null` for anything else. Capped at 18
 * digits so the value always fits Postgres int8 — beyond that the query can only be a
 * reference-string fragment anyway.
 */
function parseNumericId(trimmedQuery: string): bigint | null {
    return /^\d{1,18}$/.test(trimmedQuery) ? BigInt(trimmedQuery) : null;
}

/**
 * Whether a query is worth sending to `global.globalSearch`. Text needs the three-character floor;
 * an all-digit query of any length is searchable, because it resolves to a precise, cheap
 * id-equality lookup — incident and complaint ids are often only one or two digits.
 */
export function isRecordSearchable(query: string): boolean {
    const trimmed = query.trim();
    return trimmed.length >= RECORD_SEARCH_MIN_QUERY_LENGTH || parseNumericId(trimmed) !== null;
}

const insensitiveContains = (word: string) => ({ contains: word, mode: 'insensitive' as const });

/**
 * The matching rules for record search, in one place: every query word must match at least one of
 * the entity's searched fields (AND across words, OR across fields — typing more narrows), and an
 * all-digit query additionally matches the entity's `id` exactly, since incidents and complaints
 * have no reference string to contain it. Words too short to carry signal are dropped first.
 * Returns `null` for a query with nothing searchable.
 *
 * Each entity's field set is a documented subset of what its own list page searches — the fields a
 * record is recognised by, not every relation the list's quick filter reaches (e.g. the incident
 * list also matches owner name/email; the palette deliberately does not). Obligations are the
 * exception: they mirror their register's search box in full, including the linked-control
 * owner/tester chain, since "who owns/tests it" is how compliance users recall obligations. The
 * lists also match the
 * query as one phrase with OR semantics, where the palette ANDs across words — palette and list are
 * intentionally similar, not identical. The server skips any entity whose module is off for the
 * org, so the palette can always ask for everything.
 *
 * The ⌘K palette is the sole consumer since the top bar's search box was retired with the classic
 * bar; the rules stay here, apart from the UI, so any future record-search surface starts from the
 * same matching behaviour.
 */
export function buildGlobalSearchInput(query: string, limit: number): GlobalSearchInput | null {
    if (!isRecordSearchable(query)) {
        return null;
    }

    // A one- or two-letter word ("a", "I", "do") is a `contains` that matches nearly every row and
    // drowns the real hits. Short words carry no lookup signal anyway, so they are dropped rather
    // than allowed to blur the match — which also means a query made only of them searches for
    // nothing, unless the whole query is a number (a precise id lookup, however short).
    const trimmed = query.trim();
    const words = trimmed
        .split(/\s+/)
        .filter((word) => word.length >= RECORD_SEARCH_MIN_QUERY_LENGTH)
        .slice(0, RECORD_SEARCH_MAX_WORDS);
    const numericId = parseNumericId(trimmed);

    if (words.length === 0 && numericId === null) {
        return null;
    }

    // AND across words: each word must hit at least one field, so typing more narrows the search.
    // An all-digit query ORs the exact-id match alongside the word clauses — and a digit query too
    // short for word matching ("42") becomes the id lookup alone. The callback's return type is
    // checked against the entity's bounded where, so a field or operator outside the spec is still
    // a compile error.
    const wordAndWhere = <W>(fieldClauses: (word: string) => W[]): W | null =>
        words.length > 0 ? ({ AND: words.map((word) => ({ OR: fieldClauses(word) })) } as W) : null;

    const claimWhere = wordAndWhere<ClaimWhere>((word) => [
        { searchField: insensitiveContains(word) },
        { Insurer: { name: insensitiveContains(word) } },
    ]);

    const incidentWhere = wordAndWhere<IncidentWhere>((word) => [
        { description: insensitiveContains(word) },
        { productImpacted: insensitiveContains(word) },
        { comments: insensitiveContains(word) },
        { IncidentType: { label: insensitiveContains(word) } },
    ]);

    const complaintWhere = wordAndWhere<ComplaintWhere>((word) => [
        { complainantFirstName: insensitiveContains(word) },
        { complainantLastName: insensitiveContains(word) },
        { policyNo: insensitiveContains(word) },
        { externalClaimId: insensitiveContains(word) },
        { incidentDescription: insensitiveContains(word) },
    ]);

    const riskWhere = wordAndWhere<RiskWhere>((word) => [
        { title: insensitiveContains(word) },
        { description: insensitiveContains(word) },
    ]);

    const controlWhere = wordAndWhere<ControlWhere>((word) => [
        { name: insensitiveContains(word) },
        { objective: insensitiveContains(word) },
        { description: insensitiveContains(word) },
    ]);

    const controlTestWhere = wordAndWhere<ControlTestWhere>((word) => [
        { name: insensitiveContains(word) },
        { procedure: insensitiveContains(word) },
        { Control: { name: insensitiveContains(word) } },
    ]);

    // The register's owner/tester match: an OrgUser is found by either name part or their login
    // email. Under AND-across-words, "jane doe" resolves naturally — each word hits one name field.
    const obligationOrgUserMatch = (word: string) => ({
        OR: [
            { firstName: insensitiveContains(word) },
            { lastName: insensitiveContains(word) },
            { User: { email: insensitiveContains(word) } },
        ],
    });

    // Full parity with the obligation register's search box, deep owner/tester chain included —
    // "who owns/tests the control for this obligation" is how compliance users recall entries.
    const obligationWhere = wordAndWhere<ObligationWhere>((word) => [
        { identificationCode: insensitiveContains(word) },
        { section: insensitiveContains(word) },
        { coreObligation: insensitiveContains(word) },
        { whatItMeans: insensitiveContains(word) },
        { regulator: insensitiveContains(word) },
        { Legislation: { name: insensitiveContains(word) } },
        {
            ObligationToControl: {
                some: {
                    Control: {
                        OR: [
                            { Owner: obligationOrgUserMatch(word) },
                            { ControlTests: { some: { Tester: obligationOrgUserMatch(word) } } },
                        ],
                    },
                },
            },
        },
    ]);

    // `wordWhere` is null only for a short all-digit query, where the id clause stands alone. An
    // empty `AND: []` must never reach the server — Prisma treats it as "match everything".
    // The id clause goes through a factory typed as `(id) => W`, so "this entity's bounded where
    // accepts an exact-id filter" is compile-checked per entity rather than asserted by the cast.
    const idEquals = (id: bigint) => ({ id: { equals: id } });
    const withIdMatch = <W extends { OR?: W[] }>(wordWhere: W | null, idClause: (id: bigint) => W): W =>
        numericId === null ? (wordWhere as W) : ({ OR: [idClause(numericId), ...(wordWhere ? [wordWhere] : [])] } as W);

    return {
        limit,
        offset: 0,
        preview: true,
        // The raw text rides along for the server's claim-reference resolution ("CUR-144" →
        // CUR-000144 — contains can't bridge the zero padding). Capped to the server's bound; a
        // query that long cannot be a reference, so dropping it loses nothing.
        ...(trimmed.length <= GLOBAL_SEARCH_QUERY_MAX_LENGTH && { query: trimmed }),
        claim: { where: withIdMatch<ClaimWhere>(claimWhere, idEquals) },
        // Invoices have no numeric-id path (they are found by invoice number), so a query with no
        // usable words leaves them out entirely — the server then returns an empty envelope.
        ...(words.length > 0 && {
            invoice: {
                // `claimId` is excluded in the filter, not after the response: an
                // orphaned invoice has no page to open, and filtering client-side
                // after the server's `take` could hide navigable matches behind a
                // first page of orphans.
                where: {
                    AND: [
                        { claimId: { not: null } },
                        ...words.map((word) => ({ invoiceNo: insensitiveContains(word) }) satisfies InvoiceWhere),
                    ],
                },
            },
        }),
        incident: { where: withIdMatch<IncidentWhere>(incidentWhere, idEquals) },
        complaint: { where: withIdMatch<ComplaintWhere>(complaintWhere, idEquals) },
        risk: { where: withIdMatch<RiskWhere>(riskWhere, idEquals) },
        control: { where: withIdMatch<ControlWhere>(controlWhere, idEquals) },
        controlTest: { where: withIdMatch<ControlTestWhere>(controlTestWhere, idEquals) },
        obligation: { where: withIdMatch<ObligationWhere>(obligationWhere, idEquals) },
    };
}

/** The text a result is recognised by — what exact- and prefix-match ranking compares against. */
function matchTexts(result: RecordSearchResult): string[] {
    // Every ID-bearing entity includes String(id): the server pins an all-digit query's cited row
    // at the head of its section, and without the id in the match texts a different row whose
    // title/name happens to BE those digits would out-rank the cited record and undo the pin.
    return (
        match(result)
            .with({ entity: 'claim' }, (claim) => [claim.reference, String(claim.id)])
            .with({ entity: 'invoice' }, (invoice) => [invoice.invoiceNo])
            .with({ entity: 'incident' }, (incident) => [String(incident.id)])
            // Complaints are recalled by id or by who complained.
            .with({ entity: 'complaint' }, (complaint) => [String(complaint.id), complaint.complainantName])
            .with({ entity: 'risk' }, (risk) => [risk.title, String(risk.id)])
            .with({ entity: 'control' }, (control) => [control.name, String(control.id)])
            // Tests are recalled by their own name or the control they test.
            .with({ entity: 'controlTest' }, (controlTest) => [
                controlTest.name,
                controlTest.controlName,
                String(controlTest.id),
            ])
            .with({ entity: 'obligation' }, (obligation) => [obligation.reference, String(obligation.id)])
            .exhaustive()
    );
}

/**
 * Collapse zero-padded digit runs ("cur-000144-seed-7" → "cur-144-seed-7") so a reference ranks
 * as the user actually cites it — the server's reference resolution surfaces CUR-000144 for the
 * query "CUR-144", and without this that exact record would sit in the worst rank band below
 * fresher substring matches.
 */
const collapseZeroPadding = (text: string) => text.replace(/(^|[^0-9])0+(?=\d)/g, '$1');

/**
 * Reorder a section so the result the user most likely meant leads: an exact reference/title match
 * first, then prefix matches, then the rest — each comparison also tried with zero-padding
 * collapsed on both sides. The sort is stable, so the server's `updatedAt desc` order still
 * decides within each band. At five rows per section this is all the ranking the palette needs.
 */
function rankResults(results: RecordSearchResult[], query: string): RecordSearchResult[] {
    const normalisedQuery = query.trim().toLowerCase();
    if (!normalisedQuery) return results;
    const collapsedQuery = collapseZeroPadding(normalisedQuery);

    const band = (text: string, target: string): number => {
        if (text === target) return 0;
        if (text.startsWith(target)) return 1;
        return 2;
    };

    const score = (result: RecordSearchResult): number =>
        Math.min(
            ...matchTexts(result).flatMap((text) => {
                const normalised = text.toLowerCase();
                return [band(normalised, normalisedQuery), band(collapseZeroPadding(normalised), collapsedQuery)];
            }),
        );

    return [...results].sort((a, b) => score(a) - score(b));
}

/**
 * Flattens the procedure's per-entity envelope into render-ready sections,
 * dropping entities that matched nothing so no empty heading is drawn. Pass the
 * query so each section is ranked (exact match, then prefix, then the rest).
 */
export function toRecordSections(data: GlobalSearchOutput, query = ''): RecordSearchSection[] {
    const sections: RecordSearchSection[] = [];

    const addSection = (entity: RecordSearchResult['entity'], label: string, results: RecordSearchResult[]): void => {
        if (results.length > 0) {
            sections.push({ entity, label, results: rankResults(results, query) });
        }
    };

    addSection(
        'claim',
        'Claims',
        data.claim.rows.map(
            (row): ClaimSearchResult => ({
                entity: 'claim',
                id: row.id,
                reference: row.generatedId || String(row.id),
                statusName: row.Status.name,
                statusDisplayColor: row.Status.displayColor,
                owner: row.Owner,
            }),
        ),
    );

    // An invoice not attached to a claim has no page to open — the only route to
    // an invoice is its claim's financials tab. `buildGlobalSearchInput` already
    // excludes orphans in the filter (before the server's `take`); this is the
    // type-narrowing backstop for responses from any other input.
    addSection(
        'invoice',
        'Invoices',
        data.invoice.rows.flatMap((row): InvoiceSearchResult[] =>
            row.claimId === null ? [] : [{ entity: 'invoice', claimId: row.claimId, invoiceNo: row.invoiceNo }],
        ),
    );

    addSection(
        'incident',
        'Incidents',
        data.incident.rows.map(
            (row): IncidentSearchResult => ({
                entity: 'incident',
                id: row.id,
                description: row.description,
                status: row.status,
            }),
        ),
    );

    addSection(
        'complaint',
        'Complaints',
        data.complaint.rows.map(
            (row): ComplaintSearchResult => ({
                entity: 'complaint',
                id: row.id,
                complainantName: [row.complainantFirstName, row.complainantLastName].filter(Boolean).join(' ').trim(),
                description: row.incidentDescription,
                status: row.status,
            }),
        ),
    );

    addSection(
        'risk',
        'Risks',
        data.risk.rows.map((row): RiskSearchResult => ({ entity: 'risk', id: row.id, title: row.title })),
    );

    addSection(
        'control',
        'Controls',
        data.control.rows.map((row): ControlSearchResult => ({ entity: 'control', id: row.id, name: row.name })),
    );

    addSection(
        'controlTest',
        'Control tests',
        data.controlTest.rows.map(
            (row): ControlTestSearchResult => ({
                entity: 'controlTest',
                id: row.id,
                controlId: row.controlId,
                name: row.name,
                controlName: row.Control.name,
            }),
        ),
    );

    addSection(
        'obligation',
        'Obligations',
        data.obligation.rows.map(
            (row): ObligationSearchResult => ({
                entity: 'obligation',
                id: row.id,
                reference: row.identificationCode || String(row.id),
                coreObligation: row.coreObligation,
            }),
        ),
    );

    return sections;
}

/** Stable React key for a result — entity-scoped, since ids collide across entities. */
export function recordSearchResultKey(result: RecordSearchResult): string {
    if (result.entity === 'invoice') {
        return `invoice-${result.claimId}-${result.invoiceNo}`;
    }
    return `${result.entity}-${result.id}`;
}
